import React from 'react';
import { Box, Button, Typography, Chip, CircularProgress, Popover, IconButton } from '@mui/material';
import {
  LocalOffer as DiscountIcon,
  SwapHoriz as ReplacementIcon,
  Close as CloseIcon,
  AutoAwesome as SparkleIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../themes/typography';

const FloatingActionBar = ({
  selectedCount,
  selectedSlots = [],
  onCreateDiscount,
  onCreateReplacement,
  onAISuggest,
  aiLoading,
  onClear,
  onToggleSelect,
  onQuickDiscount
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const [expandAnchor, setExpandAnchor] = React.useState(null);
  const [quickDiscountAnchor, setQuickDiscountAnchor] = React.useState(null);

  if (selectedCount === 0) return null;

  const uniqueProducts = new Set(selectedSlots.map(s => s.d_product_id)).size;
  const isMulti = selectedCount > 1;
  const isSameProduct = uniqueProducts === 1 && isMulti;

  // Smart button labels
  const discountLabel = (() => {
    if (!isMulti) return t('discount');
    if (isSameProduct) return `${t('discount')} (${selectedCount})`;
    return `${t('machines_batchDiscount')} (${selectedCount})`;
  })();

  const replacementLabel = (() => {
    if (!isMulti) return t('replacement');
    if (isSameProduct) return `${t('replacement')} (${selectedCount})`;
    return `${t('machines_batchReplace')} (${selectedCount})`;
  })();

  const handleDiscountClick = (e) => {
    // Single slot: show quick-discount popover
    if (!isMulti && onQuickDiscount) {
      setQuickDiscountAnchor(e.currentTarget);
    } else {
      onCreateDiscount();
    }
  };

  return (
    <>
      <Box
        role="toolbar"
        aria-label={t('machines_toolbar')}
        sx={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        maxWidth: 'calc(100vw - 24px)',
        flexWrap: 'wrap',
        justifyContent: 'center',
        backgroundColor: '#1e293b',
        color: 'white',
        px: 2.5,
        py: 1.25,
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        zIndex: 1200,
        animation: 'slideUp 200ms ease-out',
        '@keyframes slideUp': {
          from: { opacity: 0, transform: 'translateX(-50%) translateY(16px)' },
          to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' }
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none'
        }
      }}>
        {/* Screen reader live region for selection count */}
        <Box aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
          {`${selectedCount} ${selectedCount === 1 ? t('slots').toLowerCase() : t('machines_slotsPlural').toLowerCase()}`}
        </Box>

        {/* Slot count + expand arrow */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Chip
            label={`${selectedCount} ${selectedCount === 1 ? t('slots').toLowerCase() : t('machines_slotsPlural').toLowerCase()}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontSize: '12px',
              fontWeight: 600,
              height: 26
            }}
          />
          <IconButton
            size="small"
            aria-label={t('machines_expandSelection')}
            aria-expanded={expanded && Boolean(expandAnchor)}
            aria-haspopup="true"
            onClick={(e) => {
              setExpandAnchor(e.currentTarget);
              setExpanded(!expanded);
            }}
            sx={{
              color: 'rgba(255,255,255,0.6)',
              p: 0.5,
              '&:hover': { color: 'white' }
            }}
          >
            {expanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Box>

        <Button
          size="small"
          startIcon={<DiscountIcon sx={{ fontSize: 16 }} />}
          onClick={handleDiscountClick}
          sx={{
            color: 'white',
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 500,
            px: 1.5,
            py: 0.5,
            borderRadius: '8px',
            backgroundColor: 'rgba(59, 130, 246, 0.3)',
            '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.5)' }
          }}
        >
          {discountLabel}
        </Button>

        <Button
          size="small"
          startIcon={<ReplacementIcon sx={{ fontSize: 16 }} />}
          onClick={onCreateReplacement}
          sx={{
            color: 'white',
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 500,
            px: 1.5,
            py: 0.5,
            borderRadius: '8px',
            backgroundColor: 'rgba(16, 185, 129, 0.3)',
            '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.5)' }
          }}
        >
          {replacementLabel}
        </Button>

        {onAISuggest && (
          <Button
            size="small"
            startIcon={aiLoading ? <CircularProgress size={14} color="inherit" /> : <SparkleIcon sx={{ fontSize: 16 }} />}
            onClick={onAISuggest}
            disabled={aiLoading}
            sx={{
              color: 'white',
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              backgroundColor: 'rgba(245, 158, 11, 0.3)',
              '&:hover': { backgroundColor: 'rgba(245, 158, 11, 0.5)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' }
            }}
          >
            {isMulti ? `${t('machines_aiSuggest')} (${selectedCount})` : t('machines_aiSuggest')}
          </Button>
        )}

        <Button
          size="small"
          onClick={onClear}
          aria-label={t('machines_clearSelection')}
          sx={{
            color: 'rgba(255,255,255,0.6)',
            minWidth: 'auto',
            px: 0.75,
            py: 0.5,
            borderRadius: '8px',
            '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </Button>
      </Box>

      {/* Expand popover: list selected slots */}
      <Popover
        open={expanded && Boolean(expandAnchor)}
        anchorEl={expandAnchor}
        onClose={() => { setExpanded(false); setExpandAnchor(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            p: 1,
            maxHeight: 200,
            overflow: 'auto',
            mb: 1,
            minWidth: 220
          }
        }}
      >
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: colors.text.muted, mb: 0.5, px: 0.5 }}>
          {t('machines_selectedSlots')}
        </Typography>
        {selectedSlots.map((slot) => (
          <Box
            key={`${slot.row}-${slot.column}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 0.5,
              px: 0.5,
              borderRadius: '4px',
              '&:hover': { backgroundColor: colors.background.subtle }
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: colors.text.primary }}>
                {slot.selection_id || `${slot.row}-${slot.column}`}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: colors.text.muted }}>
                {slot.name}
              </Typography>
            </Box>
            {onToggleSelect && (
              <IconButton
                size="small"
                aria-label={`${t('machines_deselectSlot')} ${slot.name}`}
                onClick={() => onToggleSelect(slot)}
                sx={{ p: 0.25, color: colors.text.muted, '&:hover': { color: colors.text.primary } }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        ))}
      </Popover>

      {/* Quick-discount popover (single slot only) */}
      <Popover
        open={Boolean(quickDiscountAnchor)}
        anchorEl={quickDiscountAnchor}
        onClose={() => setQuickDiscountAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: { borderRadius: '10px', p: 2, mb: 1, minWidth: 240 }
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: colors.text.primary, mb: 0.5 }}>
          {t('machines_quickDiscountTitle')}
        </Typography>
        {selectedSlots.length === 1 && (
          <Typography sx={{ fontSize: '12px', color: colors.text.muted, mb: 1 }}>
            {selectedSlots[0].name}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setQuickDiscountAnchor(null);
              onQuickDiscount?.();
            }}
            sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}
          >
            {t('machines_quickDiscountApply')}
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setQuickDiscountAnchor(null);
              onCreateDiscount();
            }}
            sx={{ textTransform: 'none', fontSize: '12px', color: colors.text.secondary }}
          >
            {t('machines_quickDiscountCustomize')}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default React.memo(FloatingActionBar);
